import { Button, Spinner, Typography } from '@ag1/ui';
import { AssurancesBaB } from '@portal/shared/components/AssurancesBaB/AssurancesBaB';
import { SoldOutProductsBanner } from '@portal/shared/components/SoldOutProductsBanner';
import { useIsStore } from '@portal/state/hooks/useIsStore';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import { SubscriptionSelectionBaBEvent, viewContentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { useStoreValue } from 'state/hooks/useStore';
import {
  checkoutStatusAtom,
  secondStepOptionAtom,
  secondStepVariantAtom,
  selectedTypeAtom
} from '../atoms';
import { ButtonStaticProps, SECOND_STEP_NAME, SubscriptionType } from '../constants';
import { useBabSanity } from '../hooks';
import { formatDecimalOrIntegerPrice, getStoreProductVariantId } from '../utils';
import { styles } from './SecondStep.css';
import { SecondStepInput } from './SecondStepInput/SecondStepInput';
import { useSessionValue } from '@portal/appLayer/auth';

export const SecondStep = () => {
  const store = useStoreValue();
  const router = useRouter();
  const [variant] = useAtom(secondStepVariantAtom);
  const [selectedVariantOption] = useAtom(secondStepOptionAtom);
  const [selectedType] = useAtom(selectedTypeAtom);
  const { theme } = useBabSanity();
  const { isUsStore, isAuStore, isNzStore } = useIsStore();
  const { currency } = useStoreCurrency();
  const session = useSessionValue();

  const {
    singleSubscription,
    doubleSubscription,
    addFamilyPlan,
    familySubscription,
    oneTime,
    switchOTP
  } = variant ?? {};

  const variantHasFamilyPlan =
    addFamilyPlan &&
    familySubscription &&
    getStoreProductVariantId(familySubscription.ProductIds, store);
  const variantName = variant?.name?.replace(' ', '-');
  const name = `${SECOND_STEP_NAME}-${variantName}`;

  const [checkoutStatus] = useAtom(checkoutStatusAtom);
  const { steps, legaleseTextSecondStep, assurances, productsInventory, variantOrder } =
    useBabSanity();
  const { secondButtonLink } = steps;
  const subAndSaveToggle = selectedType !== SubscriptionType.ONE_TIME;
  const isLoadingCheckout = checkoutStatus === 'loading';
  const showOTPValueLegalese = isUsStore || isAuStore || isNzStore;

  useEffect(() => {
    if (
      !selectedVariantOption?.product ||
      !selectedVariantOption?.product.ProductIds?.length ||
      !router.isReady
    )
      return;

    const { ProductIds, price, productTitle, productType, quantity } =
      selectedVariantOption.product;
    const finalPrice = parseFloat(price || '0');

    const productId = (ProductIds.find((id) => id.targetStore === store) || ProductIds[0])
      ?.productVariantId;

    const eventId = `second-step-${Date.now()}`;

    viewContentEvent({
      contents: [
        {
          id: productId,
          title: productTitle,
          category: productType,
          price: finalPrice,
          quantity
        }
      ],
      eventId,
      currency,
      value: finalPrice,
      store,
      user: {
        email: session?.customer.email || ''
      }
    });

    SubscriptionSelectionBaBEvent({
      itemId: productId,
      itemName: productTitle,
      subscriptionType: selectedType,
      store,
      clickText: 'Checkout',
      location: router.asPath
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantOption?.product.productTitle]);

  const updateQuery = useCallback(
    (newQuery: object) => {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            ...newQuery
          }
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  const sortedOfferCards = useMemo(() => {
    const sections = [
      {
        key: 'singleSubscription',
        component: singleSubscription && (
          <SecondStepInput
            key="singleSubscription"
            name={name}
            checked={selectedType === SubscriptionType.SINGLE}
            onChange={() => {
              updateQuery({ type: SubscriptionType.SINGLE });
            }}
            product={singleSubscription}
            isOneTime={false}
            isDouble={false}
            basePrice={parseInt(singleSubscription.oldPrice, 10)}
            price={parseInt(singleSubscription.price, 10)}
            pricePerServing={singleSubscription.pricePerServing}
            priceTextMobile={singleSubscription.priceTextMobile}
            priceTextDesktop={singleSubscription.priceTextDesktop}
          />
        )
      },
      {
        key: 'doubleSubscription',
        component: doubleSubscription && (
          <SecondStepInput
            key="doubleSubscription"
            name={name}
            checked={selectedType === SubscriptionType.DOUBLE}
            onChange={() => updateQuery({ type: SubscriptionType.DOUBLE })}
            product={doubleSubscription}
            isOneTime={false}
            isDouble
            basePrice={parseInt(doubleSubscription.oldPrice, 10)}
            price={parseInt(doubleSubscription.price, 10)}
            pricePerServing={doubleSubscription.pricePerServing}
            priceTextMobile={doubleSubscription.priceTextMobile}
            priceTextDesktop={doubleSubscription.priceTextDesktop}
          />
        )
      },
      {
        key: 'familySubscription',
        component: variantHasFamilyPlan && (
          <SecondStepInput
            key="familySubscription"
            name={name}
            checked={selectedType === SubscriptionType.FAMILY_PLAN}
            onChange={() => {
              updateQuery({ type: SubscriptionType.FAMILY_PLAN });
            }}
            product={familySubscription}
            isOneTime={false}
            isDouble={false}
            basePrice={parseInt(familySubscription.oldPrice, 10)}
            price={formatDecimalOrIntegerPrice(familySubscription.price)}
            pricePerServing={familySubscription.pricePerServing}
            priceTextMobile={familySubscription.priceTextMobile}
            priceTextDesktop={familySubscription.priceTextDesktop}
          />
        )
      },
      {
        key: 'oneTime',
        component: oneTime && switchOTP && (
          <SecondStepInput
            key="oneTime"
            name={name}
            checked={selectedType === SubscriptionType.ONE_TIME}
            onChange={() => {
              updateQuery({ type: SubscriptionType.ONE_TIME });
            }}
            product={oneTime}
            isOneTime
            isDouble={false}
            basePrice={parseInt(oneTime.oldPrice, 10)}
            price={parseInt(oneTime.price, 10)}
          />
        )
      }
    ];

    if (!variantOrder) return sections.map((section) => section.component).filter(Boolean);

    const sorted = variantOrder
      .map((vName) => sections.find((section) => section.key === vName)?.component)
      .filter(Boolean);

    return sorted;
  }, [
    singleSubscription,
    name,
    selectedType,
    doubleSubscription,
    variantHasFamilyPlan,
    familySubscription,
    oneTime,
    switchOTP,
    variantOrder,
    updateQuery
  ]);

  return (
    <>
      <div className={styles.cards}>{sortedOfferCards}</div>
      {productsInventory.length > 0 && (
        <SoldOutProductsBanner productsInventory={productsInventory} />
      )}
      <div className={styles.buttonContainer}>
        {showOTPValueLegalese && (
          <Typography
            noPadding
            variant="portalCaption"
            size="1"
            fontStyle="italic"
            className={clsx(
              styles.disclaimer,
              getThemeCSSClass(theme === 'family_plan' ? 'new' : theme)
            )}>
            <sup className={styles.disclaimerSup}>†</sup> Value of one-time purchase
          </Typography>
        )}
        <Button
          {...ButtonStaticProps}
          theme="new"
          size={{ xxs: 'medium', s: 'large' }}
          variant={secondButtonLink?.variant || 'primary'}
          disabled={isLoadingCheckout}
          icon={
            secondButtonLink.showIcon && !isLoadingCheckout && secondButtonLink.iconOptions?.icon
          }
          iconPosition={secondButtonLink.iconOptions?.position}>
          {isLoadingCheckout ? (
            <Spinner color="brand-white" size={24} withOverlay={false} />
          ) : (
            secondButtonLink.portableText && (
              <CustomPortableText value={secondButtonLink.portableText} />
            )
          )}
        </Button>
      </div>
      {subAndSaveToggle && assurances && (
        <AssurancesBaB assurances={assurances} className={styles.assurances} />
      )}
      {legaleseTextSecondStep && (
        <div className={styles.legaleseTextContainer}>
          {legaleseTextSecondStep.map((text) => (
            <Typography
              key={text}
              noPadding
              size="3"
              variant="body"
              className={styles.legaleseText}>
              {text}
            </Typography>
          ))}
        </div>
      )}
    </>
  );
};
